import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { fileUrl, MUTATION_EVENT } from "@/lib/api";

const PlayerContext = createContext(null);

const RECENT_KEY = "lxn_recent_songs_v1";
function pushRecent(song) {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    const filtered = raw.filter((s) => s.id !== song.id);
    filtered.unshift({ id: song.id, name: song.name, artwork_id: song.artwork_id, at: Date.now() });
    localStorage.setItem(RECENT_KEY, JSON.stringify(filtered.slice(0, 10)));
  } catch {}
}
export function getRecent() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
}

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null); // {song, version}
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  // Queue for continuous playback (EP/project)
  const [queue, setQueue] = useState([]); // array of song objects
  const [queueIndex, setQueueIndex] = useState(-1);
  // A/B
  const [abMode, setAbMode] = useState(false);
  const [abA, setAbA] = useState(null);
  const [abB, setAbB] = useState(null);
  const [abSide, setAbSide] = useState("A");
  // Phase 6.8i — immersive player chrome state + playback modes
  const [fullPlayerOpen, setFullPlayerOpen] = useState(false);
  const openFullPlayer = useCallback(() => setFullPlayerOpen(true), []);
  const closeFullPlayer = useCallback(() => setFullPlayerOpen(false), []);
  const [shuffle, setShuffle] = useState(false);
  // repeat: "off" | "all" | "one"
  const [repeat, setRepeat] = useState("off");
  const [volume, setVolume] = useState(1);
  const toggleShuffle = useCallback(() => setShuffle((s) => !s), []);
  const cycleRepeat = useCallback(() => setRepeat((r) => r === "off" ? "all" : r === "all" ? "one" : "off"), []);
  const setVol = useCallback((v) => {
    const vv = Math.max(0, Math.min(1, v));
    setVolume(vv);
    if (audioRef.current) audioRef.current.volume = vv;
  }, []);
  // Apply volume when audio element is created / changed
  useEffect(() => { if (audioRef.current) audioRef.current.volume = volume; }, [volume, current]);

  // In-memory cache of a song → latest version so tapping Play on a song
  // already listed once no longer round-trips /versions before audio can
  // start. Cache is scoped to the module and reset whenever versions
  // legitimately change (create/update/delete emits below).
  const versionCacheRef = useRef(new Map()); // songId -> version
  const findLatestVersion = useCallback(async (songId) => {
    const cached = versionCacheRef.current.get(songId);
    if (cached) return cached;
    const { data } = await api.get(`/songs/${songId}/versions`);
    if (!data.length) return null;
    // Default playback = newest upload (FINAL is not preferred).
    const target = [...data].sort((a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""))[0];
    if (target) versionCacheRef.current.set(songId, target);
    return target;
  }, []);
  // Public helper the rest of the app can call to bust the cache when a new
  // version is uploaded or an existing one is edited/deleted.
  const invalidateSongVersions = useCallback((songId) => {
    if (songId) versionCacheRef.current.delete(songId);
    else versionCacheRef.current.clear();
  }, []);
  useEffect(() => {
    const onMut = () => versionCacheRef.current.clear();
    window.addEventListener(MUTATION_EVENT, onMut);
    return () => window.removeEventListener(MUTATION_EVENT, onMut);
  }, []);

  useEffect(() => {
    if (!audioRef.current) {
      const a = new Audio();
      a.preload = "auto";
      // NOTE: crossOrigin was previously "anonymous", which forced an extra
      // CORS preflight (OPTIONS) on EVERY /api/files/{id} play and added
      // hundreds of ms of latency before the first byte arrived. We don't do
      // Web Audio analysis on this element, so a plain same-origin request
      // (which /api is, thanks to the ingress) starts streaming immediately
      // and Range requests unlock progressive playback.
      audioRef.current = a;
    }
    const a = audioRef.current;
    const onTime = () => setProgress(a.currentTime);
    const onDur = () => setDuration(a.duration || 0);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onDur);
    a.addEventListener("durationchange", onDur);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onDur);
      a.removeEventListener("durationchange", onDur);
    };
  }, []);

  // Prefetch effect (run when queue/index/current change)
  useEffect(() => {

    // Prefetch first byte of the NEXT queued track to warm cache & Range
    if (queue.length && queueIndex >= 0 && queueIndex + 1 < queue.length) {
      const nextSong = queue[queueIndex + 1];
      if (nextSong?.id) {
        (async () => {
          try {
            const target = await findLatestVersion(nextSong.id);
            if (target?.file_id) {
              try {
                await fetch(fileUrl(target.file_id), { headers: { Range: "bytes=0-65535" } });
              } catch {}
            }
          } catch {}
        })();
      }
    }
  }, [current, queue, queueIndex, findLatestVersion]);

  const playVersion = useCallback((song, version) => {
    if (!version || !version.file_id) return;
    const a = audioRef.current;
    if (current?.version?.id === version.id) {
      // Same track — just resume without reloading (huge perceived win).
      a.play().then(() => setPlaying(true)).catch(() => {});
      return;
    }
    const url = fileUrl(version.file_id);
    a.src = url;
    // Optimistic UI: reflect intent immediately. If the browser rejects it
    // (usually only when there's no user gesture), we correct on error.
    setCurrent({ song, version });
    setProgress(0);
    setPlaying(true);
    pushRecent(song);
    a.play().catch(() => setPlaying(false));
  }, [current]);

  // Play song by loading its latest version (used everywhere outside Versions tab)
  const playSong = useCallback(async (song) => {
    try {
      const v = await findLatestVersion(song.id);
      if (!v) return;
      playVersion(song, v);
    } catch {}
  }, [findLatestVersion, playVersion]);

  // Play a queue (list of songs) starting at given index. Each plays its latest version.
  const playQueue = useCallback(async (songs, startIndex = 0) => {
    setQueue(songs);
    setQueueIndex(startIndex);
    if (songs[startIndex]) await playSong(songs[startIndex]);
  }, [playSong]);

  const nextTrack = useCallback(async () => {
    // Phase 6.8i — honour shuffle + repeat. "one" is handled by the ended
    // handler below so Next-tap on repeat-one still advances naturally.
    if (!queue.length) { setPlaying(false); return; }
    if (shuffle && queue.length > 1) {
      let ni = queueIndex;
      // avoid immediate repeat of same track
      for (let tries = 0; tries < 6; tries++) {
        const cand = Math.floor(Math.random() * queue.length);
        if (cand !== queueIndex) { ni = cand; break; }
      }
      setQueueIndex(ni);
      await playSong(queue[ni]);
      return;
    }
    if (queueIndex + 1 < queue.length) {
      const ni = queueIndex + 1;
      setQueueIndex(ni);
      await playSong(queue[ni]);
    } else if (repeat === "all") {
      setQueueIndex(0);
      await playSong(queue[0]);
    } else {
      setPlaying(false);
    }
  }, [queue, queueIndex, shuffle, repeat, playSong]);

  // Queue management helpers (Phase 6.8i)
  const addToQueue = useCallback((song) => {
    setQueue((q) => [...q, song]);
  }, []);
  const removeFromQueue = useCallback((idx) => {
    setQueue((q) => {
      const n = q.filter((_, i) => i !== idx);
      // adjust index if removing a track at/before current
      if (idx < queueIndex) setQueueIndex((i) => Math.max(0, i - 1));
      if (idx === queueIndex && n.length === 0) { setQueueIndex(-1); }
      return n;
    });
  }, [queueIndex]);
  const reorderQueue = useCallback((from, to) => {
    setQueue((q) => {
      const n = [...q];
      const [m] = n.splice(from, 1);
      n.splice(to, 0, m);
      // keep queueIndex pointing at the same song
      if (queueIndex === from) setQueueIndex(to);
      else if (from < queueIndex && to >= queueIndex) setQueueIndex((i) => i - 1);
      else if (from > queueIndex && to <= queueIndex) setQueueIndex((i) => i + 1);
      return n;
    });
  }, [queueIndex]);
  const clearQueue = useCallback(() => { setQueue([]); setQueueIndex(-1); }, []);

  const prevTrack = useCallback(async () => {
    const a = audioRef.current;
    if (a && a.currentTime > 3) { a.currentTime = 0; return; }
    if (queue.length && queueIndex > 0) {
      const ni = queueIndex - 1;
      setQueueIndex(ni);
      await playSong(queue[ni]);
    } else if (a) {
      a.currentTime = 0;
    }
  }, [queue, queueIndex, playSong]);

  // ended handler needs latest nextTrack; wire separately
  useEffect(() => {
    const a = audioRef.current; if (!a) return;
    const onEnd = () => {
      if (repeat === "one") {
        // Loop the same track on natural end.
        a.currentTime = 0;
        a.play().then(() => setPlaying(true)).catch(() => {});
      } else {
        nextTrack();
      }
    };
    a.addEventListener("ended", onEnd);
    return () => a.removeEventListener("ended", onEnd);
  }, [nextTrack, repeat]);

  const pause = useCallback(() => { audioRef.current?.pause(); setPlaying(false); }, []);
  const toggle = useCallback(() => {
    if (!current) return;
    if (playing) pause();
    else audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
  }, [current, playing, pause]);
  const seek = useCallback((t) => {
    if (audioRef.current) { audioRef.current.currentTime = t; setProgress(t); }
  }, []);

  const startAB = useCallback((song, a, b) => {
    setAbMode(true); setAbA({ song, version: a }); setAbB({ song, version: b }); setAbSide("A");
    playVersion(song, a);
  }, [playVersion]);
  const switchAB = useCallback(() => {
    if (!abMode || !abA || !abB) return;
    const t = audioRef.current?.currentTime || 0;
    const next = abSide === "A" ? "B" : "A";
    const nextV = next === "A" ? abA : abB;
    audioRef.current.src = fileUrl(nextV.version.file_id);
    audioRef.current.currentTime = t;
    audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
    setCurrent(nextV); setAbSide(next);
  }, [abMode, abA, abB, abSide]);
  const stopAB = useCallback(() => { setAbMode(false); setAbA(null); setAbB(null); }, []);

  return (
    <PlayerContext.Provider value={{
      current, playing, progress, duration,
      queue, queueIndex,
      play: playVersion, playSong, playQueue, nextTrack, prevTrack,
      pause, toggle, seek,
      abMode, abA, abB, abSide, startAB, switchAB, stopAB,
      invalidateSongVersions,
      // Phase 6.8i
      fullPlayerOpen, openFullPlayer, closeFullPlayer,
      shuffle, repeat, toggleShuffle, cycleRepeat,
      volume, setVolume: setVol,
      addToQueue, removeFromQueue, reorderQueue, clearQueue,
      findLatestVersion,
    }}>
      {children}
    </PlayerContext.Provider>
  );
}

export const usePlayer = () => useContext(PlayerContext);
